/**
 * `db.from(…)` replacement — a query builder over `/v1/db/:table`.
 *
 * WHY A BUILDER AND NOT 457 REWRITTEN CALL SITES
 * ----------------------------------------------
 * The app makes 457 `.from()` calls. Rewriting each into a bespoke fetch would
 * be an unreviewable diff and would change the shape every hook depends on
 * (`{ data, error }`, `.single()`, `.select()` chaining). This keeps the chain
 * intact and moves only the transport, so migrating a hook is:
 *
 *     import { db } from "./db-client";
 *     import { db } from "@lyder-talk/core/lib/db-client";
 *     …
 *     db.from("videos")  →  db.from("videos")
 *
 * The builder deliberately supports **only what the app actually uses**
 * (measured: eq/neq/gt/gte/lt/lte/like/ilike/is/not/in/contains, order, limit,
 * range, single, maybeSingle, count, head, insert, update, upsert, delete).
 * Anything else throws instead of silently reading the wrong rows.
 */

import { auth } from "./auth-client";

const REQUEST_TIMEOUT_MS = 8_000;

export interface DbResult<T = any> {
  /**
   * Rows, a single row, or null.
   *
   * `any` on purpose: the previous client was generated against a database type
   * map, so call sites read `row.some_column` directly. Typing this as `unknown`
   * pushed ~90 property-access errors into untouched files without improving a
   * single one of them. Columns are validated server-side against
   * `information_schema`, so the risk this hides is a typo, not a bad query.
   */
  data: T | null;
  error: { message: string; code?: string; status?: number } | null;
  count?: number;
}

interface BuilderState {
  op?: "select" | "insert" | "update" | "delete" | "upsert";
  columns?: string;
  filters: { column: string; op: string; value?: unknown }[];
  or?: string;
  order: { column: string; ascending?: boolean }[];
  limit?: number;
  offset?: number;
  returning?: string;
  single?: "one" | "maybe";
  count?: "exact";
  head?: boolean;
  values?: Record<string, unknown> | Record<string, unknown>[];
  patch?: Record<string, unknown>;
  onConflict?: string;
  /** `.upsert(values, { ignoreDuplicates: true })` — keep the existing row. */
  ignoreDuplicates?: boolean;
}

function apiBaseUrl(): string | null {
  const value = import.meta.env.VITE_API_BASE_URL as string | undefined;
  return value ? value.replace(/\/+$/, "") : null;
}

class QueryBuilder implements PromiseLike<DbResult> {
  private state: BuilderState = { filters: [], order: [] };
  private execution: Promise<DbResult> | null = null;

  constructor(private readonly table: string) {}

  /* ------------------------------------------------------------- shaping */

  select(columns: string = "*", options?: { count?: "exact"; head?: boolean }): this {
    // After an insert/update/delete this is `returning`, not a new read.
    const write = this.state.op === "insert" || this.state.op === "update" || this.state.op === "upsert" || this.state.op === "delete";
    if (write) this.state.returning = columns;
    else {
      this.state.op = "select";
      this.state.columns = columns;
    }
    if (options?.count) this.state.count = options.count;
    if (options?.head) this.state.head = true;
    return this;
  }

  /* ------------------------------------------------------------- filters */

  private addFilter(column: string, op: string, value?: unknown): this {
    this.state.filters.push({ column, op, value });
    return this;
  }

  eq(column: string, value: unknown): this {
    return this.addFilter(column, "eq", value);
  }
  neq(column: string, value: unknown): this {
    return this.addFilter(column, "neq", value);
  }
  gt(column: string, value: unknown): this {
    return this.addFilter(column, "gt", value);
  }
  gte(column: string, value: unknown): this {
    return this.addFilter(column, "gte", value);
  }
  lt(column: string, value: unknown): this {
    return this.addFilter(column, "lt", value);
  }
  lte(column: string, value: unknown): this {
    return this.addFilter(column, "lte", value);
  }
  like(column: string, value: string): this {
    return this.addFilter(column, "like", value);
  }
  ilike(column: string, value: string): this {
    return this.addFilter(column, "ilike", value);
  }

  is(column: string, value: null | boolean): this {
    return this.addFilter(column, "is", value);
  }

  in(column: string, values: unknown[]): this {
    return this.addFilter(column, "in", values);
  }

  contains(column: string, value: unknown): this {
    return this.addFilter(column, "contains", value);
  }

  /** `.not("col", "is", null)` — the only not-form the app uses. */
  not(column: string, operator: string, value: unknown): this {
    if (operator !== "is") throw new Error(`db-client: unsupported .not("${operator}")`);
    return this.addFilter(column, "not", value);
  }

  /** `.match({a: 1, b: 2})` is sugar for two equality filters. */
  match(criteria: Record<string, unknown>): this {
    for (const [column, value] of Object.entries(criteria)) this.addFilter(column, "eq", value);
    return this;
  }

  /** PostgREST or-syntax, passed through to the gateway's parser. */
  or(expression: string): this {
    this.state.or = this.state.or ? `${this.state.or},${expression}` : expression;
    return this;
  }

  /* -------------------------------------------------------------- paging */

  order(column: string, options?: { ascending?: boolean }): this {
    this.state.order.push({ column, ascending: options?.ascending });
    return this;
  }

  limit(count: number): this {
    this.state.limit = count;
    return this;
  }

  range(from: number, to: number): this {
    this.state.offset = from;
    this.state.limit = to - from + 1;
    return this;
  }

  /* ------------------------------------------------------------- writes */

  insert(values: Record<string, unknown> | Record<string, unknown>[]): this {
    this.state.op = "insert";
    this.state.values = values;
    return this;
  }

  upsert(
    values: Record<string, unknown> | Record<string, unknown>[],
    options?: { onConflict?: string; ignoreDuplicates?: boolean },
  ): this {
    this.state.op = "upsert";
    this.state.values = values;
    this.state.onConflict = options?.onConflict;
    this.state.ignoreDuplicates = options?.ignoreDuplicates ?? false;
    return this;
  }

  update(patch: Record<string, unknown>): this {
    this.state.op = "update";
    this.state.patch = patch;
    return this;
  }

  delete(): this {
    this.state.op = "delete";
    return this;
  }

  /* ----------------------------------------------------------- execution */

  /** `.single()` → exactly one row, or an error (PGRST116). */
  single(): PromiseLike<DbResult> {
    this.state.single = "one";
    return this;
  }

  /** `.maybeSingle()` → zero or one row. */
  maybeSingle(): PromiseLike<DbResult> {
    this.state.single = "maybe";
    return this;
  }

  then<TResult1 = DbResult, TResult2 = never>(
    onfulfilled?: ((value: DbResult) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
  ): Promise<TResult1 | TResult2> {
    const promise = this.execute();
    return onfulfilled || onrejected ? promise.then(onfulfilled, onrejected) : (promise as Promise<TResult1>);
  }

  private execute(): Promise<DbResult> {
    // A builder can be awaited more than once (some hooks do); run it once so a
    // double await cannot perform a double insert.
    if (this.execution) return this.execution;

    this.execution = this.run();
    return this.execution;
  }

  private async run(): Promise<DbResult> {
    const base = apiBaseUrl();
    if (!base) return { data: null, error: { message: "VITE_API_BASE_URL is not set" } };

    const token = auth.getAccessToken();
    // Captured before the request: the guard below compares the *issuing* account
    // with the one that owns the token at retry time.
    const userId = auth.getUserId();
    let attempt = await this.send(base, token);

    // The access token lives an hour and nothing refreshes it in the background,
    // so a tab left open past that boundary sends a dead token to every query.
    // The API answers 401, which surfaces here as `data: null` — the page then
    // renders "no data" while still showing the signed-in learner. Rotate once
    // and replay the same request; a token that is still refused is a real auth
    // failure and stays an error.
    if (attempt.status === 401) {
      // The account is part of the retry decision: if the session changed while
      // this request was in flight, replaying it would run the query as whoever
      // signed in next.
      const fresh = await auth.refreshAccessToken(token, userId);
      if (fresh && fresh !== token) {
        attempt = await this.send(base, fresh);
      }
    }

    return attempt.result;
  }

  /** One HTTP round trip. `status` is null when the request never got a reply. */
  private async send(base: string, token: string | null): Promise<{ result: DbResult; status: number | null }> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(`${base}/v1/db/${this.table}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ op: "select", ...this.state }),
        signal: controller.signal,
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        return {
          status: response.status,
          result: {
            data: null,
            error: {
              message: payload?.message ?? `${response.status} ${response.statusText}`,
              code: payload?.code,
              status: response.status,
            },
          },
        };
      }

      return { status: response.status, result: { data: payload?.data ?? null, count: payload?.count, error: null } };
    } catch (error) {
      return {
        status: null,
        result: {
          data: null,
          error: {
            message:
              error instanceof DOMException && error.name === "AbortError"
                ? `db ${this.table} timed out`
                : error instanceof Error
                  ? error.message
                  : "Network error",
          },
        },
      };
    } finally {
      clearTimeout(timer);
    }
  }
}

/** PostgREST-shaped client. `db.from(table)` replaces `db.from(table)`. */
export const db = {
  from(table: string): QueryBuilder {
    return new QueryBuilder(table);
  },
};

export type { QueryBuilder };
